import React from 'react';
import { FiEye, FiHeart, FiShoppingCart, FiStar } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import toast from 'react-hot-toast';

const ProductCard = ({ product, showDiscount = false }) => {
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const navigate = useNavigate();

  const discountPercentage =
    showDiscount && product.originalPrice
      ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
      : 0;

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    toast.success('Added to cart');
  };

  const handleWishlistToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product);
  };

  const handleQuickView = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/product/${product.id}`);
  };

  return (
    <Link to={`/product/${product.id}`} className="group block h-full" data-stagger-item>
      <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:border-slate-300 hover:shadow-float">
        <div className="relative overflow-hidden bg-slate-50">
          {showDiscount && discountPercentage > 0 && (
            <span className="absolute left-3 top-3 z-10 rounded-md bg-[#DB4444] px-2 py-1 text-[11px] font-bold text-white">
              -{discountPercentage}%
            </span>
          )}

          <div className="absolute right-3 top-3 z-10 flex flex-col gap-2 opacity-0 transition duration-300 group-hover:opacity-100">
            <button
              onClick={handleWishlistToggle}
              className={`rounded-full border p-2 backdrop-blur ${
                isInWishlist(product.id)
                  ? 'border-[#DB4444] bg-[#DB4444] text-white'
                  : 'border-slate-200 bg-white/90 text-slate-700 hover:border-[#DB4444] hover:text-[#DB4444]'
              }`}
              title={isInWishlist(product.id) ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              <FiHeart className={isInWishlist(product.id) ? 'fill-current' : ''} size={14} />
            </button>
            <button
              onClick={handleQuickView}
              className="rounded-full border border-slate-200 bg-white/90 p-2 text-slate-700 backdrop-blur transition hover:border-[#DB4444] hover:text-[#DB4444]"
              title="Quick view"
            >
              <FiEye size={14} />
            </button>
          </div>

          <img
            src={product.image || '/placeholder-product.jpg'}
            alt={product.name}
            className="h-56 w-full object-cover transition duration-500 group-hover:scale-105"
          />

          <button
            onClick={handleAddToCart}
            className="absolute inset-x-4 bottom-4 flex translate-y-8 items-center justify-center gap-2 rounded-full bg-[#1A1A1A] px-4 py-2.5 text-sm font-semibold text-white opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-100 hover:bg-[#DB4444]"
          >
            <FiShoppingCart size={14} />
            Add to Cart
          </button>
        </div>

        <div className="flex flex-1 flex-col p-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
            {product.category?.replace(/-/g, ' ') || 'Product'}
          </p>
          <h3 className="mt-1 line-clamp-2 text-base font-semibold text-[#1A1A1A] group-hover:text-[#DB4444]">
            {product.name}
          </h3>

          <div className="mt-3 flex items-center gap-2">
            <span className="text-lg font-extrabold text-[#1A1A1A]">৳{product.price}</span>
            {product.originalPrice && (
              <span className="text-sm text-slate-400 line-through">৳{product.originalPrice}</span>
            )}
          </div>

          <div className="mt-2 flex items-center justify-between">
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <FiStar
                  key={i}
                  size={13}
                  className={i < (product.rating || 0) ? 'fill-current text-amber-400' : 'text-slate-300'}
                />
              ))}
            </div>
            <span className="text-xs font-semibold text-slate-500">({product.reviews || 0})</span>
          </div>
        </div>
      </article>
    </Link>
  );
};

export default ProductCard;
